import { z } from 'zod';

// Personal Information form -- the left-card top section of the Settings
// screen. Email is read-only (it's the auth identity; a real change-email
// flow lives behind a confirm-and-reverify endpoint not built yet) so it's
// not in the schema. Phone is intentionally optional + permissive: the
// designer's mock shows "+44 59683345" but country prefixes vary wildly
// and we don't want to reject legitimate input. Tighten when there's a
// country picker.
export const profileSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(100),
  lastName: z.string().trim().min(1, 'Last name is required').max(100),
  phone: z.string().trim().max(40, 'Phone number is too long').optional().or(z.literal('')),
});

export type ProfileInput = z.infer<typeof profileSchema>;

// Security & Password form. Rules mirror the four bullets in the on-page
// "Security Requirements" panel (min 8, uppercase, digit, special) so a
// user can self-check while typing. Confirm field matches new via refine.
const PASSWORD_REQUIREMENT_REGEX = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z
      .string()
      .regex(
        PASSWORD_REQUIREMENT_REGEX,
        'Must be at least 8 chars with an uppercase, a digit, and a special character',
      ),
    confirmPassword: z.string().min(1, 'Confirm your new password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match',
  });

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// Organization Settings form. Mirrors the onboarding CreateTenant schema's
// `name` + `description`. Branding color + logo are not in the schema --
// the color is a hex string in component state, and the logo is a File
// handled via the file input (preview only until the upload API lands).
export const orgSettingsSchema = z.object({
  name: z.string().trim().min(1, 'Organization name is required').max(200),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
});

export type OrgSettingsInput = z.infer<typeof orgSettingsSchema>;
